import React from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";
import Tooltip from "@atlaskit/tooltip";

import { wacToken } from "@/libraries/design/foundations";

// Icon styles for the button
const iconStyles = css({
    "display": "inline-flex",
    "alignItems": "center",
    "justifyContent": "center",
    "width": "32px",
    "height": "32px",
    "borderRadius": "50%",
    "border": "none",
    "cursor": "pointer",
    "transition": "background-color 0.2s ease-in-out",
    "padding": 0,
    "flexShrink": 0,
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "4px"
    }
});

// Disabled state styles
const disabledStyles = css({
    "backgroundColor": token("color.background.neutral"),
    "color": "#080F2147",
    "cursor": "default",
    "&:hover": {
        backgroundColor: token("color.background.neutral"),
        color: "#080F2147" // eslint-disable-line local/ensure-wac-token-usage
    }
});

// Enabled state styles
const enabledStyles = css({
    "backgroundColor": wacToken("color.primary.blue"),
    "color": token("color.text.inverse"),
    "&:hover": {
        backgroundColor: wacToken("color.extended.blue900")
    },
    "&:active": {
        backgroundColor: wacToken("color.extended.blue800")
    }
});

export type RovoIconButtonProps = {
    isDisabled?: boolean;
    onClick?: () => void;
    icon?: React.ReactNode;
    ariaLabel?: string;
    tooltip?: string;
};

export const RovoIconButton: React.FC<RovoIconButtonProps> = ({
    isDisabled = true,
    onClick,
    icon,
    ariaLabel = "Submit",
    tooltip
}) => {
    const button = (
        <button
            css={[iconStyles, isDisabled ? disabledStyles : enabledStyles]}
            onClick={isDisabled ? undefined : onClick}
            disabled={isDisabled}
            aria-label={ariaLabel}
        >
            {icon}
        </button>
    );

    if (tooltip) {
        // Wrap disabled buttons in a span so tooltip works
        if (isDisabled) {
            return (
                <Tooltip content={tooltip}>
                    <span style={{ display: "inline-block" }}>{button}</span>
                </Tooltip>
            );
        }
        return <Tooltip content={tooltip}>{button}</Tooltip>;
    }

    return button;
};
